import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { parseLocalDate } from '../../../utils/dateUtils';
import { toDateInputValue } from '../../../utils/dateUtils';


const ROW_TYPE_CONFIG = {
  deliverable: { label: 'Deliverable', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: 'Package' },
  task: { label: 'Task', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: 'CheckSquare' },
  subtask: { label: 'Sub-task', color: 'bg-green-100 text-green-800 border-green-200', icon: 'CornerDownRight' }
};

const AddDeliverableModal = ({ isOpen, onClose, onSave, editingDeliverable, availableResources, existingDeliverables, defaultRowType, defaultParentId, defaultPhaseId, project, phases }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    estimatedHours: '',
    workEstimate: '',
    priority: 'medium',
    status: 'pending',
    rowType: defaultRowType || 'deliverable',
    parentId: defaultParentId || '',
    phaseId: defaultPhaseId || '',
    assignedResources: [],
    resourceDates: {},
    dependsOn: []
  });

  const [errors, setErrors] = useState({});
  const [showResourcePicker, setShowResourcePicker] = useState(false);
  const [showDependencyPicker, setShowDependencyPicker] = useState(false);
  const [resourceFilter, setResourceFilter] = useState('');
  const [resourceCategoryFilter, setResourceCategoryFilter] = useState('all');
  const [resourceStatusFilter, setResourceStatusFilter] = useState('all');
  const [resourceSortBy, setResourceSortBy] = useState('name');
  const [resourceSortDir, setResourceSortDir] = useState('asc');

  const getDefaultDates = (rowType, parentId, phaseId, proj, deliverables, phases) => {
    if (rowType === 'deliverable') {
      // Default to phase dates if phase is selected
      if (phaseId) {
        const phase = phases?.find(p => p?.id === phaseId);
        if (phase) return { startDate: phase?.startDate || proj?.startDate || '', endDate: phase?.endDate || proj?.endDate || '' };
      }
      return { startDate: proj?.startDate || '', endDate: proj?.endDate || '' };
    } else if (rowType === 'task' || rowType === 'subtask') {
      const parent = deliverables?.find(d => d?.id === parentId);
      return { startDate: parent?.startDate || '', endDate: parent?.endDate || '' };
    }
    return { startDate: '', endDate: '' };
  };

  useEffect(() => {
    if (editingDeliverable) {
      // Build resourceDates map from existing assignedResources
      const resourceDatesMap = {};
      (editingDeliverable?.assignedResources || [])?.forEach(r => {
        if (r?.id) {
          resourceDatesMap[r.id] = {
            startDate: r?.allocationStartDate || editingDeliverable?.startDate || '',
            endDate: r?.allocationEndDate || editingDeliverable?.endDate || ''
          };
        }
      });
      setFormData({
        name: editingDeliverable?.name || '',
        description: editingDeliverable?.description || '',
        startDate: toDateInputValue(editingDeliverable?.startDate),
        endDate: toDateInputValue(editingDeliverable?.endDate),
        estimatedHours: editingDeliverable?.estimatedHours?.toString() || '',
        workEstimate: editingDeliverable?.workEstimate || '',
        priority: editingDeliverable?.priority || 'medium',
        status: editingDeliverable?.status || 'pending',
        rowType: editingDeliverable?.rowType || 'deliverable',
        parentId: editingDeliverable?.parentId || '',
        phaseId: editingDeliverable?.phaseId || '',
        assignedResources: editingDeliverable?.assignedResources?.map(r => r?.id) || [],
        resourceDates: resourceDatesMap,
        dependsOn: editingDeliverable?.dependsOn || []
      });
    } else {
      const defaultDates = getDefaultDates(defaultRowType || 'deliverable', defaultParentId || '', defaultPhaseId || '', project, existingDeliverables, phases || []);
      setFormData({
        name: '',
        description: '',
        startDate: defaultDates?.startDate,
        endDate: defaultDates?.endDate,
        estimatedHours: '',
        workEstimate: '',
        priority: 'medium',
        status: 'pending',
        rowType: defaultRowType || 'deliverable',
        parentId: defaultParentId || '',
        phaseId: defaultPhaseId || '',
        assignedResources: [],
        resourceDates: {},
        dependsOn: []
      });
    }
    setErrors({});
    setShowResourcePicker(false);
    setShowDependencyPicker(false);
  }, [editingDeliverable, isOpen, defaultRowType, defaultParentId]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = 'Name is required';
    if (!formData?.startDate) newErrors.startDate = 'Start date is required';
    if (!formData?.endDate) newErrors.endDate = 'End date is required';
    if (formData?.startDate && formData?.endDate && parseLocalDate(formData?.startDate) > parseLocalDate(formData?.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }
    if ((formData?.rowType === 'task' || formData?.rowType === 'subtask') && !formData?.parentId) {
      newErrors.parentId = formData?.rowType === 'task' ? 'Please select a parent deliverable' : 'Please select a parent task';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      try {
        const saveData = {
          ...formData,
          estimatedHours: formData?.estimatedHours ? parseFloat(formData?.estimatedHours) : null,
          parentId: formData?.parentId || null,
          phaseId: formData?.phaseId || null
        };
        if (editingDeliverable) {
          await onSave(editingDeliverable?.id, saveData);
        } else {
          await onSave(saveData);
          onClose();
        }
      } catch (err) {
        setErrors({ submit: err?.message || 'Failed to save' });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'parentId' && value && (prev?.rowType === 'task' || prev?.rowType === 'subtask')) {
        const parent = existingDeliverables?.find(d => d?.id === value);
        if (parent) {
          updated.startDate = parent?.startDate || prev?.startDate;
          updated.endDate = parent?.endDate || prev?.endDate;
          // Update resource dates that still match old parent dates
          const newResourceDates = { ...prev?.resourceDates };
          prev?.assignedResources?.forEach(rid => {
            if (!newResourceDates?.[rid] || (newResourceDates?.[rid]?.startDate === prev?.startDate && newResourceDates?.[rid]?.endDate === prev?.endDate)) {
              newResourceDates[rid] = { startDate: parent?.startDate || '', endDate: parent?.endDate || '' };
            }
          });
          updated.resourceDates = newResourceDates;
        }
      }
      return updated;
    });
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const toggleResource = (resourceId) => {
    setFormData(prev => {
      const isSelected = prev?.assignedResources?.includes(resourceId);
      if (isSelected) {
        const newDates = { ...prev?.resourceDates };
        delete newDates?.[resourceId];
        return { ...prev, assignedResources: prev?.assignedResources?.filter(id => id !== resourceId), resourceDates: newDates };
      } else {
        return {
          ...prev,
          assignedResources: [...prev?.assignedResources, resourceId],
          resourceDates: {
            ...prev?.resourceDates,
            [resourceId]: { startDate: prev?.startDate || '', endDate: prev?.endDate || '' }
          }
        };
      }
    });
  };

  const handleResourceDateChange = (resourceId, field, value) => {
    setFormData(prev => ({
      ...prev,
      resourceDates: {
        ...prev?.resourceDates,
        [resourceId]: { ...prev?.resourceDates?.[resourceId], [field]: value }
      }
    }));
  };

  const toggleDependency = (deliverableId) => {
    setFormData(prev => ({
      ...prev,
      dependsOn: prev?.dependsOn?.includes(deliverableId)
        ? prev?.dependsOn?.filter(id => id !== deliverableId)
        : [...prev?.dependsOn, deliverableId]
    }));
  };

  const getSelectedResources = () => availableResources?.filter(r => formData?.assignedResources?.includes(r?.id));
  const getSelectedDependencies = () => existingDeliverables?.filter(d => formData?.dependsOn?.includes(d?.id));

  const resourceCategories = [...new Set(availableResources?.map(r => r?.category)?.filter(Boolean))];
  const resourceStatuses = [...new Set(availableResources?.map(r => r?.status)?.filter(Boolean))];

  const toggleSort = (field) => {
    if (resourceSortBy === field) {
      setResourceSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setResourceSortBy(field);
      setResourceSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (resourceSortBy !== field) return <Icon name="ChevronsUpDown" size={12} className="text-muted-foreground" />;
    return resourceSortDir === 'asc'
      ? <Icon name="ChevronUp" size={12} className="text-accent" />
      : <Icon name="ChevronDown" size={12} className="text-accent" />;
  };

  const filteredResources = availableResources
    ?.filter(resource =>
      (resource?.name?.toLowerCase()?.includes(resourceFilter?.toLowerCase()) ||
        resource?.category?.toLowerCase()?.includes(resourceFilter?.toLowerCase())) &&
      (resourceCategoryFilter === 'all' || resource?.category === resourceCategoryFilter) &&
      (resourceStatusFilter === 'all' || resource?.status?.toLowerCase() === resourceStatusFilter?.toLowerCase())
    )
    ?.sort((a, b) => {
      let valA = a?.[resourceSortBy] || '';
      let valB = b?.[resourceSortBy] || '';
      if (typeof valA === 'string') valA = valA?.toLowerCase();
      if (typeof valB === 'string') valB = valB?.toLowerCase();
      if (valA < valB) return resourceSortDir === 'asc' ? -1 : 1;
      if (valA > valB) return resourceSortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const parentOptions = formData?.rowType === 'task'
    ? existingDeliverables?.filter(d => d?.rowType === 'deliverable' && d?.id !== editingDeliverable?.id)
    : formData?.rowType === 'subtask'
    ? existingDeliverables?.filter(d => d?.rowType === 'task' && d?.id !== editingDeliverable?.id)
    : [];

  const availableDependencies = existingDeliverables?.filter(d => d?.id !== editingDeliverable?.id);

  const rowTypeConfig = ROW_TYPE_CONFIG?.[formData?.rowType] || ROW_TYPE_CONFIG?.deliverable;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-foreground">
              {editingDeliverable ? 'Edit' : 'Add'} {rowTypeConfig?.label}
            </h2>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${rowTypeConfig?.color}`}>
              <Icon name={rowTypeConfig?.icon} size={12} />
              {rowTypeConfig?.label}
            </span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errors?.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              {errors?.submit}
            </div>
          )}

          {/* Project Context Block */}
          {project && (
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="FolderOpen" size={16} className="text-primary" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Project Context</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <div className="sm:col-span-1">
                  <p className="text-xs text-muted-foreground mb-0.5">Project</p>
                  <p className="text-sm font-semibold text-foreground">{project?.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Start Date</p>
                  <p className="text-sm font-medium text-foreground">
                    {project?.startDate ? parseLocalDate(project?.startDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">End Date</p>
                  <p className="text-sm font-medium text-foreground">
                    {project?.endDate ? parseLocalDate(project?.endDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </p>
                </div>
              </div>
              {existingDeliverables?.filter(d => d?.rowType === 'deliverable')?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Deliverables</p>
                  <div className="flex flex-wrap gap-1.5">
                    {existingDeliverables?.filter(d => d?.rowType === 'deliverable')?.map(d => (
                      <span key={d?.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-800 border border-purple-200 rounded-full text-xs font-medium">
                        <Icon name="Package" size={10} />
                        {d?.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Row Type Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Type</label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(ROW_TYPE_CONFIG)?.map(([type, config]) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    const defaultDates = getDefaultDates(type, '', '', project, existingDeliverables, []);
                    setFormData(prev => ({
                      ...prev,
                      rowType: type,
                      parentId: '',
                      startDate: defaultDates?.startDate,
                      endDate: defaultDates?.endDate
                    }));
                  }}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                    formData?.rowType === type
                      ? `border-primary bg-primary/5 text-primary`
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  <Icon name={config?.icon} size={16} />
                  {config?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Parent Selection for tasks and subtasks */}
          {(formData?.rowType === 'task' || formData?.rowType === 'subtask') && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {formData?.rowType === 'task' ? 'Parent Deliverable' : 'Parent Task'}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                name="parentId"
                value={formData?.parentId}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-input border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                  errors?.parentId ? 'border-red-500' : 'border-border'
                }`}
              >
                <option value="">Select {formData?.rowType === 'task' ? 'deliverable' : 'task'}...</option>
                {parentOptions?.map(opt => (
                  <option key={opt?.id} value={opt?.id}>{opt?.name}</option>
                ))}
              </select>
              {errors?.parentId && <p className="mt-1 text-xs text-red-600">{errors?.parentId}</p>}
              {parentOptions?.length === 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {formData?.rowType === 'task' ? 'No deliverables exist yet. Create a deliverable first.' : 'No tasks exist yet. Create a task first.'}
                </p>
              )}
            </div>
          )}

          {/* Phase Assignment for deliverables */}
          {formData?.rowType === 'deliverable' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Assign to Phase</label>
              {phases?.length > 0 ? (
                <>
                  <select
                    name="phaseId"
                    value={formData?.phaseId || ''}
                    onChange={(e) => {
                      const selectedProjectPhaseId = e?.target?.value;
                      setFormData(prev => {
                        const updated = { ...prev, phaseId: selectedProjectPhaseId };
                        if (selectedProjectPhaseId) {
                          const phase = phases?.find(p => p?.id === selectedProjectPhaseId);
                          if (phase) {
                            updated.startDate = phase?.startDate || prev?.startDate;
                            updated.endDate = phase?.endDate || prev?.endDate;
                          }
                        }
                        return updated;
                      });
                    }}
                    className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">No phase (unassigned)</option>
                    {phases?.map(phase => (
                      <option key={phase?.id} value={phase?.id}>
                        {phase?.name} ({phase?.startDate ? parseLocalDate(phase?.startDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?'} – {phase?.endDate ? parseLocalDate(phase?.endDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?'})
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <p className="text-sm text-muted-foreground bg-muted/50 border border-border rounded-lg px-4 py-3">
                  No phases have been added to this project yet. Add phases first to assign this deliverable to one.
                </p>
              )}
            </div>
          )}

          <Input
            label={`${rowTypeConfig?.label} Name`}
            type="text"
            name="name"
            value={formData?.name}
            onChange={handleChange}
            placeholder={`e.g., ${formData?.rowType === 'deliverable' ? 'Complete foundation work' : formData?.rowType === 'task' ? 'Prepare site drawings' : 'Review structural plans'}`}
            error={errors?.name}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
            <textarea
              name="description"
              value={formData?.description}
              onChange={handleChange}
              placeholder="Describe the details and requirements..."
              rows={3}
              className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Start Date"
              type="date"
              name="startDate"
              value={formData?.startDate}
              onChange={handleChange}
              error={errors?.startDate}
              required
            />
            <Input
              label="End Date"
              type="date"
              name="endDate"
              value={formData?.endDate}
              onChange={handleChange}
              error={errors?.endDate}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Estimated Hours <span className="text-muted-foreground text-xs">(optional)</span>
              </label>
              <input
                type="number"
                name="estimatedHours"
                value={formData?.estimatedHours}
                onChange={handleChange}
                placeholder="e.g., 8"
                min="0"
                step="0.5"
                className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Work Estimate <span className="text-muted-foreground text-xs">(optional)</span>
              </label>
              <input
                type="text"
                name="workEstimate"
                value={formData?.workEstimate}
                onChange={handleChange}
                placeholder="e.g., 2 days, 1 week"
                className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Priority</label>
              <select
                name="priority"
                value={formData?.priority}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Status</label>
              <select
                name="status"
                value={formData?.status}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="pending">Not Started</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>
          </div>

          {/* Assigned Resources */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Assigned Resources</label>
            {getSelectedResources()?.length > 0 && (
              <div className="flex flex-col gap-2 mb-3">
                {getSelectedResources()?.map((resource) => (
                  <div key={resource?.id} className="border border-border rounded-lg p-3 bg-primary/5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-primary">{resource?.name}</span>
                      <button type="button" onClick={() => toggleResource(resource?.id)} className="hover:bg-primary/20 rounded-full p-0.5 flex-shrink-0">
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">Start Date</label>
                        <input
                          type="date"
                          value={formData?.resourceDates?.[resource?.id]?.startDate || ''}
                          onChange={(e) => handleResourceDateChange(resource?.id, 'startDate', e?.target?.value)}
                          className="w-full px-2 py-1.5 bg-input border border-border rounded text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">End Date</label>
                        <input
                          type="date"
                          value={formData?.resourceDates?.[resource?.id]?.endDate || ''}
                          onChange={(e) => handleResourceDateChange(resource?.id, 'endDate', e?.target?.value)}
                          className="w-full px-2 py-1.5 bg-input border border-border rounded text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button type="button" variant="outline" size="sm" onClick={() => setShowResourcePicker(!showResourcePicker)} iconName={showResourcePicker ? 'ChevronUp' : 'ChevronDown'} iconPosition="right">
              {showResourcePicker ? 'Hide' : 'Select'} Resources
            </Button>
            {showResourcePicker && (
              <div className="mt-3 border border-border rounded-lg overflow-hidden">
                <div className="p-3 border-b border-border space-y-2">
                  <input
                    type="text"
                    placeholder="Search by name or category..."
                    value={resourceFilter}
                    onChange={(e) => setResourceFilter(e?.target?.value)}
                    className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={resourceCategoryFilter}
                      onChange={(e) => setResourceCategoryFilter(e?.target?.value)}
                      className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="all">All Categories</option>
                      {resourceCategories?.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <select
                      value={resourceStatusFilter}
                      onChange={(e) => setResourceStatusFilter(e?.target?.value)}
                      className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="all">All Statuses</option>
                      {resourceStatuses?.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">Sort by:</span>
                    {['name', 'category', 'status']?.map(field => (
                      <button
                        key={field}
                        type="button"
                        onClick={() => toggleSort(field)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                          resourceSortBy === field
                            ? 'bg-accent/10 text-accent border border-accent/30' :'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        {field?.charAt(0)?.toUpperCase() + field?.slice(1)}
                        <SortIcon field={field} />
                      </button>
                    ))}
                    <span className="ml-auto text-xs text-muted-foreground">{filteredResources?.length} result{filteredResources?.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                <div className="p-3 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {filteredResources?.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">No resources match the filters</p>
                    ) : (
                      filteredResources?.map((resource) => (
                        <label key={resource?.id} className="flex items-center gap-3 p-2 hover:bg-muted rounded-lg cursor-pointer">
                          <input type="checkbox" checked={formData?.assignedResources?.includes(resource?.id)} onChange={() => toggleResource(resource?.id)} className="w-4 h-4 rounded border-input" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">{resource?.name}</p>
                            <p className="text-xs text-muted-foreground">{resource?.category} • {resource?.identifier}</p>
                          </div>
                          {resource?.status && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                              resource?.status?.toLowerCase() === 'available' ?'bg-green-100 text-green-700'
                                : resource?.status?.toLowerCase() === 'in use' || resource?.status?.toLowerCase() === 'in-use'
                                ? 'bg-amber-100 text-amber-700' :'bg-muted text-muted-foreground'
                            }`}>{resource?.status}</span>
                          )}
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Dependencies */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Dependencies</label>
            {getSelectedDependencies()?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {getSelectedDependencies()?.map((deliverable) => (
                  <div key={deliverable?.id} className="flex items-center gap-2 px-3 py-1 bg-accent/10 text-accent rounded-full text-sm">
                    <Icon name="GitBranch" size={12} />
                    <span>{deliverable?.name}</span>
                    <button type="button" onClick={() => toggleDependency(deliverable?.id)} className="hover:bg-accent/20 rounded-full p-0.5">
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Button type="button" variant="outline" size="sm" onClick={() => setShowDependencyPicker(!showDependencyPicker)} iconName={showDependencyPicker ? 'ChevronUp' : 'ChevronDown'} iconPosition="right">
              {showDependencyPicker ? 'Hide' : 'Select'} Dependencies
            </Button>
            {showDependencyPicker && (
              <div className="mt-3 border border-border rounded-lg p-4 max-h-48 overflow-y-auto">
                {availableDependencies?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No other items available</p>
                ) : (
                  <div className="space-y-2">
                    {availableDependencies?.map((d) => (
                      <label key={d?.id} className="flex items-center gap-3 p-2 hover:bg-muted rounded-lg cursor-pointer">
                        <input type="checkbox" checked={formData?.dependsOn?.includes(d?.id)} onChange={() => toggleDependency(d?.id)} className="w-4 h-4 rounded border-input" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                              d?.rowType === 'deliverable' ? 'bg-purple-100 text-purple-700' :
                              d?.rowType === 'task' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                            }`}>{ROW_TYPE_CONFIG?.[d?.rowType]?.label || 'Deliverable'}</span>
                            <p className="text-sm font-medium text-foreground">{d?.name}</p>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {parseLocalDate(d?.startDate)?.toLocaleDateString()} - {parseLocalDate(d?.endDate)?.toLocaleDateString()}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">
              {editingDeliverable ? 'Update' : 'Create'} {rowTypeConfig?.label}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDeliverableModal;